/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react';
import type { DivProps } from 'typings';
import UDInput from 'ud-form/components/input/component';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import * as S from './styles';

type SampleId = 'confirm' | 'confirm-broken-buttons' | 'confirm-long-name' | 'form' | 'form-wide' | 'notice' | 'notice-long-text';

interface SampleMeta {
  id: SampleId;
  title: string;
  kind: string;
  source: string;
}

const SAMPLES: SampleMeta[] = [
  {
    id: 'confirm',
    title: 'Подтверждение — канон',
    kind: 'Окно подтверждения',
    source: 'Modal.Confirm · size="sm" (440px)\nud-ui/components/modal/confirm-modal.tsx',
  },
  {
    id: 'confirm-broken-buttons',
    title: 'Подтверждение — «Да»/«Нет»',
    kind: 'Антипример',
    source: 'Обе кнопки primary, «Да» слева\nтак было в remove-confirm-modal.tsx до фазы 5',
  },
  {
    id: 'confirm-long-name',
    title: 'Подтверждение — длинное имя',
    kind: 'Окно подтверждения',
    source: 'Modal.Confirm · заголовок переносится, ширина не растёт\nбыло: stage-group.tsx:156 (w-[450px])',
  },
  {
    id: 'form',
    title: 'Форма — средняя',
    kind: 'Окно-форма',
    source: 'Header/Body/Footer · size="md" (560px) — дефолт\ngame-event-creator.tsx:61, timestampUrls.tsx:85',
  },
  {
    id: 'form-wide',
    title: 'Форма — широкая',
    kind: 'Окно-форма',
    source: 'Header/Body/Footer · size="lg" (760px)\ncrop-modal.tsx:134, text-block-help.tsx:27',
  },
  {
    id: 'notice',
    title: 'Уведомление',
    kind: 'Окно-уведомление',
    source: 'size="sm", Footer justify="center"\nsuccess-copy.tsx:16, notice.tsx:41',
  },
  {
    id: 'notice-long-text',
    title: 'Подтверждение — длинное пояснение',
    kind: 'Окно подтверждения',
    source: 'Modal.Confirm · description с переносами\nuse-delete-template-modal.tsx',
  },
];

const LONG_TEAM_NAME = 'Объединённый спортивный клуб «Северная звезда — Академия 2007»';

const LONG_DESCRIPTION =
  'Шаблон будет удалён без возможности восстановления.\n\nВсе изображения, сгенерированные по нему ранее, останутся на месте.';

interface IProps extends DivProps {}

function ModalsPage(props: IProps) {
  const { ...rest } = props;
  const { setHeaderContent } = useMainLayout();
  const [openSample, setOpenSample] = useState<SampleId | null>(null);

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Модальные окна</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const close = useCallback(() => setOpenSample(null), []);

  return (
    <S.MainBlock {...rest}>
      <S.Intro>
        Витрина модальных окон (IMGM-1427, план <code>.claude/plans/modal-unification.md</code>). Окна подтверждения собраны
        каноническим <code>Modal.Confirm</code>, окна-формы и уведомления — из <code>Modal.Header</code> / <code>Modal.Body</code> /{' '}
        <code>Modal.Footer</code> с фиксированной шириной через проп <code>size</code> (<code>sm</code> 440px, <code>md</code> 560px,{' '}
        <code>lg</code> 760px). Образец «Да»/«Нет» оставлен антипримером — так окна выглядели до унификации. Под подписью каждого
        образца указан файл-источник и то, что в нём ещё не переведено.
      </S.Intro>

      <S.Grid>
        {SAMPLES.map((sample) => (
          <S.Card key={sample.id}>
            <S.CardTitle>{sample.title}</S.CardTitle>
            <S.CardKind>{sample.kind}</S.CardKind>
            <S.CardSource>{sample.source}</S.CardSource>
            <S.CardAction>
              <UDButton variant="secondary" onClick={() => setOpenSample(sample.id)} stretch>
                Открыть
              </UDButton>
            </S.CardAction>
          </S.Card>
        ))}
      </S.Grid>

      {/* Канон окна подтверждения */}
      <Modal.Confirm
        show={openSample === 'confirm'}
        title="Удалить команду «Метеор»?"
        description="Это действие необратимо."
        onConfirm={close}
        onCancel={close}
      />

      {/* Антипример: нарушенный порядок и цвет кнопок, remove-confirm-modal.tsx:40 (чинится в фазе 5) */}
      <Modal show={openSample === 'confirm-broken-buttons'} onClose={close}>
        <Modal.Body className={'text-center'}>
          <UDText type="headline">Удалить изображение?</UDText>
        </Modal.Body>
        <Modal.Footer justify="center">
          <UDButton variant="primary" onClick={close} style={{ width: 144 }}>
            Да
          </UDButton>
          <UDButton variant="primary" onClick={close} style={{ width: 144 }}>
            Нет
          </UDButton>
        </Modal.Footer>
      </Modal>

      {/* Длинное имя: ширина фиксирована size="sm", заголовок переносится по словам */}
      <Modal.Confirm
        show={openSample === 'confirm-long-name'}
        title={`Удалить команду «${LONG_TEAM_NAME}»?`}
        description="Это действие необратимо."
        onConfirm={close}
        onCancel={close}
      />

      {/* Форма средней ширины: game-event-creator.tsx:63 */}
      <Modal show={openSample === 'form'} onClose={close} size="md">
        <Modal.Header title="Добавить событие матча" />
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <UDInput placeholder="Минута" />
            <UDInput placeholder="Игрок" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="secondary" onClick={close}>
            Отмена
          </UDButton>
          <UDButton variant="primary" onClick={close}>
            Сохранить
          </UDButton>
        </Modal.Footer>
      </Modal>

      {/* Широкая форма: stage-matches.tsx:285 */}
      <Modal show={openSample === 'form-wide'} onClose={close} size="lg">
        <Modal.Header title="Настройки матча" subtitle="Дата, время, локация и составы команд" />
        <Modal.Body>
          <div className="grid grid-cols-2 gap-4">
            <UDInput placeholder="Дата" />
            <UDInput placeholder="Время" />
            <UDInput placeholder="Хозяева" />
            <UDInput placeholder="Гости" />
            <UDInput placeholder="Локация" />
            <UDInput placeholder="Судья" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="secondary" onClick={close}>
            Отмена
          </UDButton>
          <UDButton variant="primary" onClick={close}>
            Сохранить
          </UDButton>
        </Modal.Footer>
      </Modal>

      {/* Уведомление: devices-page.tsx:244 */}
      <Modal show={openSample === 'notice'} onClose={close} size="sm">
        <Modal.Header title="Ссылка на табло перенесена" />
        <Modal.Body>
          <UDText variant="body-3" className="center mb-2">
            Ссылка на табло больше не привязана к матчу — теперь она принадлежит устройству.
          </UDText>
          <UDText variant="body-3" className="center">
            Старый формат ссылок больше не поддерживается.
          </UDText>
        </Modal.Body>
        <Modal.Footer justify={'center'}>
          <UDButton variant="primary" onClick={close} stretch>
            Понятно
          </UDButton>
        </Modal.Footer>
      </Modal>

      {/* Длинное пояснение: description переносится, ширина остаётся sm */}
      <Modal.Confirm
        show={openSample === 'notice-long-text'}
        title="Удалить шаблон «Расписание тура»?"
        description={LONG_DESCRIPTION}
        onConfirm={close}
        onCancel={close}
      />
    </S.MainBlock>
  );
}

export default ModalsPage;
